import {
  Button,
  ErrorBoundary,
  Icon,
  IconButton,
  SearchInput,
  Select,
  Text,
  HStack,
  VStack,
} from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import { Box, Center, Pressable } from 'native-base';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { StyleSheet } from 'react-native';
import { FlatList } from 'react-native-gesture-handler';
import { useRecoilValue } from 'recoil';

import { useRecoilQuery } from '../../../../hooks/useRecoilQuery';
import { useMasterStateActions } from '../../../../store/master/actions';
import {
  masterStateAdvisorListLoaded,
  masterStateSelectedAdvisorSelector,
  masterStateSelectedPartnerSelector,
  masterStateSelector,
} from '../../../../store/master/selector';
import { sessionState } from '../../../../store/session/atom';
import { ImpersonateMenuContext } from '../../context';
import { ImpersonateMenuAction, ImpersonateMenuComponent, ImpersonateMenuPage } from '../../types';

import { AdvisorOption } from './components/AdvisorOption';
import { EmptyListLoading } from './components/EmptyListLoading';
import { EmptyListNotFound } from './components/EmptyListNotFound';

const horizontalSpacing = 'inline.xxxs';

const styles = StyleSheet.create({
  flatListContainer: {
    flex: 1,
  },
  flatListContent: {
    gap: Utils.Token.ToInt(Spacing, 'inline.xxxs'),
    paddingHorizontal: Utils.Token.ToInt(Spacing, horizontalSpacing),
  },
});

export const ImpersonateSelection: React.FC<ImpersonateMenuComponent> = ({ width, onAction }) => {
  const { userProfile, isMaster } = useRecoilValue(sessionState);
  const { data, loading, error, refresher } = useRecoilQuery(masterStateSelector({ isMaster }));
  const { queueActionAfterClose, confirmationCallback } = useContext(ImpersonateMenuContext);
  const advisorListLoaded = useRecoilValue(masterStateAdvisorListLoaded);
  const selectedAdvisor = useRecoilValue(masterStateSelectedAdvisorSelector);
  const selectedPartner = useRecoilValue(masterStateSelectedPartnerSelector);
  const [search, setSearch] = useState('');

  const handleSearch = (value: string) => {
    setSearch(value);
  };

  const handleClearSearch = () => {
    setSearch('');
  };

  const [currentSelectedAdvisor, setCurrentSelectedAdvisor] = useState(selectedAdvisor);
  const [currentSelectedPartner, setCurrentSelectedPartner] = useState(selectedPartner);
  const { setMasterState, setNewSelectedAdvisor, setNewSelectedPartner, clearSelectedAdvisor } =
    useMasterStateActions();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleChangeMaster = () => {
    if (!data) {
      return;
    }

    const advisorToBeSet = data?.advisorList.find(
      (advisor) => advisor.id === currentSelectedAdvisor
    );

    if (!advisorToBeSet) {
      clearSelectedAdvisor();
    } else {
      if (confirmationCallback) {
        queueActionAfterClose(async () => {
          const confirmed = await confirmationCallback();
          if (confirmed) {
            setNewSelectedPartner(currentSelectedPartner);
            setNewSelectedAdvisor(advisorToBeSet.id);
          }
        });
      } else {
        queueActionAfterClose(() => {
          setNewSelectedPartner(currentSelectedPartner);
          setNewSelectedAdvisor(advisorToBeSet.id);
        });
      }

      onAction(ImpersonateMenuAction.Close);
    }
  };

  const handleChangePartner = useCallback((newSelectedPartner: string) => {
    setCurrentSelectedPartner(parseInt(newSelectedPartner, 10));
  }, []);

  const handleGoBack = () => {
    onAction(ImpersonateMenuAction.Transition, ImpersonateMenuPage.User);
  };

  const handleClose = () => {
    onAction(ImpersonateMenuAction.Close);
  };

  const partnerListMemoized = useMemo(() => {
    if (!data || !data.partnerList) {
      return [];
    }

    return data.partnerList.map((partner) => ({
      label: partner.name || '',
      value: partner.id.toString(),
    }));
  }, [data]);

  const advisorListMemoized = useMemo(() => {
    if (!data || !data.advisorList) {
      return [];
    }

    return data.advisorList
      .filter((advisor) => advisor.name.toLowerCase().includes(search.toLowerCase()))
      .filter((advisor) => advisor.partnerId === currentSelectedPartner)
      .map((advisor) => ({
        label: advisor.name || '',
        value: advisor.id,
        selected: advisor.id === currentSelectedAdvisor,
      }));
  }, [data, search, currentSelectedPartner, currentSelectedAdvisor]);

  const handleClearSelection = () => {
    setCurrentSelectedAdvisor(undefined);
  };

  useEffect(() => {
    if (!data || advisorListLoaded) {
      return;
    }

    setMasterState(data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    setCurrentSelectedPartner(selectedPartner);
  }, [selectedPartner]);

  useEffect(() => {
    setCurrentSelectedAdvisor(selectedAdvisor);
  }, [selectedAdvisor]);

  useEffect(() => {
    if (currentSelectedAdvisor === undefined) {
      handleChangeMaster();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelectedAdvisor]);

  const content = useMemo(() => {
    if (error) {
      return (
        <Center paddingX={Spacing[horizontalSpacing]} flex={1}>
          <ErrorBoundary errorMessageProps={{ textAlign: 'center' }} onRefresh={refresher} />
        </Center>
      );
    }

    return (
      <>
        <VStack space={Spacing['squish.nano']} paddingX={Spacing[horizontalSpacing]}>
          <Text flex={1} fontWeight={Typography['fontweight.bold']}>
            Visualizando por:
          </Text>
          <Select
            optionsDesktopDirection="right"
            placeholder="Selecione uma filial"
            options={partnerListMemoized}
            selectedValue={currentSelectedPartner?.toString()}
            onValueChange={handleChangePartner}
          />
          <SearchInput
            isMobile
            hideSearchButtonOnDesktop
            placeholder="Pesquisar por nome"
            onSearch={handleSearch}
            onClear={handleClearSearch}
            fullWidthAlways
          />
        </VStack>
        <HStack paddingX={Spacing[horizontalSpacing]} alignItems="center">
          <Text flex={1} fontWeight={Typography['fontweight.bold']}>
            Visualizar dados de:
          </Text>
          <Pressable
            flexDirection="row"
            alignItems="center"
            style={{ gap: Utils.Token.ToInt(Spacing, 'stack.nano') }}
            onPress={handleClearSelection}>
            <Text
              color="neutral.medium"
              fontWeight={Typography['fontweight.semibold']}
              fontSize={Typography['fontsize.xs']}>
              LIMPAR SELEÇÃO
            </Text>
            <Icon name="Trash" size="xxs" color={Color['neutral.medium']} />
          </Pressable>
        </HStack>
        <FlatList
          style={styles.flatListContainer}
          data={advisorListMemoized}
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.flatListContent}
          ListEmptyComponent={loading ? EmptyListLoading : EmptyListNotFound}
          renderItem={({ item }) => (
            <AdvisorOption {...item} onSelect={setCurrentSelectedAdvisor} />
          )}
        />
        <Box
          borderTopColor={Color['neutral.lightest']}
          borderTopWidth={1}
          padding={Spacing[horizontalSpacing]}>
          <Button
            variant="primary"
            isDisabled={currentSelectedAdvisor === undefined || loading}
            onPress={handleChangeMaster}>
            Aplicar
          </Button>
        </Box>
      </>
    );
  }, [
    advisorListMemoized,
    currentSelectedAdvisor,
    currentSelectedPartner,
    error,
    handleChangeMaster,
    handleChangePartner,
    loading,
    partnerListMemoized,
    refresher,
  ]);

  return (
    <VStack flex={1} width={width} space={Spacing['stack.xxxs']} paddingTop={Spacing['inset.sm']}>
      <HStack paddingX={Spacing[horizontalSpacing]} alignItems="center">
        <IconButton
          onPress={handleGoBack}
          icon="ArrowLeft"
          iconSize="xs"
          testId="back"
          iconColor={Color['neutral.black']}
        />
        <Text flex={1} fontWeight={Typography['fontweight.bold']}>
          Visualizar dados de:
        </Text>
        <IconButton
          onPress={handleClose}
          icon="Close"
          iconSize="xxs"
          testId="back"
          iconColor={Color['neutral.black']}
        />
      </HStack>
      <HStack
        marginX={Spacing[horizontalSpacing]}
        padding={Spacing['inline.xxxs']}
        space={Spacing['stack.nano']}
        borderRadius={BoxRadius.sm}
        backgroundColor={Utils.ColorToken.WithOpacity('feedback.gen.lightest', 0.55)}
        alignItems="center">
        <Icon name="IdCard" size="xs" color={Color['brand.tertiary.principal']} />
        <Text>Perfil de acesso</Text>
        <Text flex={1} textAlign="right" fontWeight={Typography['fontweight.semibold']}>
          {userProfile?.masterParceiro ? 'MASTER PARCEIRO' : 'MASTER GRUPO'}
        </Text>
      </HStack>
      {content}
    </VStack>
  );
};

ImpersonateSelection.displayName = 'ImpersonateSelection';
